import {
  floorLabel,
  resolveCategory,
  type DeductionTable,
  type Discipline,
  type DisciplineCategory,
  type Floor,
  type Recording,
  type ScoringSheet,
} from "./queries/rules";
import { RESULT_FORMULA_LABELS, formatTimeRange } from "./rulesLabels";
import type { ID } from "./id";

/**
 * What a combination changes relative to its discipline's defaults.
 *
 * The matrix is mostly inheritance — a handful of cells deviate and the
 * rest follow the discipline — so the useful thing to show in a cell is
 * not its resolved values (they would be identical almost everywhere)
 * but the delta. Each entry carries both sides so the UI can show the
 * new value and keep the old one within reach.
 */
export interface OverrideDiff {
  /** Stable key, for React lists. */
  key: string;
  label: string;
  /** The discipline default this cell departs from. */
  from: string;
  /** What the cell uses instead. */
  to: string;
}

/** Renders the paired judge counts the way the detail page does. */
const judges = (plus: number, tech: number) => `${plus} + ${tech}`;

/**
 * Describes a combination's overrides.
 *
 * The time limit and the judge counts are each reported as one entry
 * rather than two: they are stored as separate columns but read as a
 * single fact ("1:15–1:30", "3 + 1"), and splitting them would show a
 * change to the upper bound as a mysterious half-diff.
 */
export function describeOverrides(
  discipline: Discipline,
  category: DisciplineCategory,
  lookup: {
    deductionTables: DeductionTable[];
    scoringSheets: ScoringSheet[];
    floors: Floor[];
    recordings: Recording[];
  },
): OverrideDiff[] {
  const resolved = resolveCategory(discipline, category);
  const out: OverrideDiff[] = [];

  if (category.time_min_seconds !== undefined || category.time_max_seconds !== undefined) {
    out.push({
      key: "time",
      label: "Čas",
      from: formatTimeRange(
        discipline.default_time_min_seconds ?? null,
        discipline.default_time_max_seconds ?? null,
      ),
      to: formatTimeRange(resolved.time_min_seconds, resolved.time_max_seconds),
    });
  }

  if (category.floor_id !== undefined) {
    out.push({
      key: "floor_id",
      label: "Plocha",
      from: floorName(lookup.floors, discipline.default_floor_id),
      to: floorName(lookup.floors, category.floor_id),
    });
  }

  if (category.recording_id !== undefined) {
    out.push({
      key: "recording_id",
      label: "Hudba",
      from: recordingName(lookup.recordings, discipline.default_recording_id),
      to: recordingName(lookup.recordings, category.recording_id),
    });
  }

  if (category.plus_judges !== undefined || category.tech_judges !== undefined) {
    out.push({
      key: "judges",
      label: "Porota",
      from: judges(discipline.default_plus_judges, discipline.default_tech_judges),
      to: judges(resolved.plus_judges, resolved.tech_judges),
    });
  }

  if (category.result_formula !== undefined) {
    out.push({
      key: "result_formula",
      label: "Výsledek",
      from: RESULT_FORMULA_LABELS[discipline.default_result_formula],
      to: RESULT_FORMULA_LABELS[category.result_formula],
    });
  }

  if (category.deduction_table_id !== undefined) {
    out.push({
      key: "deduction_table_id",
      label: "Srážky",
      from: tableName(lookup.deductionTables, discipline.default_deduction_table_id),
      to: tableName(lookup.deductionTables, category.deduction_table_id),
    });
  }

  if (category.scoring_sheet_id !== undefined) {
    out.push({
      key: "scoring_sheet_id",
      label: "Hodnocení",
      from: sheetName(lookup.scoringSheets, discipline.default_scoring_sheet_id),
      to: sheetName(lookup.scoringSheets, category.scoring_sheet_id),
    });
  }

  if (category.note !== "") {
    out.push({ key: "note", label: "Poznámka", from: "—", to: category.note });
  }

  return out;
}

function floorName(floors: Floor[], id: ID | undefined): string {
  if (id === undefined) return "nenastaveno";
  const floor = floors.find((f) => f.id === id);
  return floor ? floorLabel(floor) : "neznámá";
}

function recordingName(recordings: Recording[], id: ID | undefined): string {
  if (id === undefined) return "nenastaveno";
  return recordings.find((r) => r.id === id)?.name ?? "neznámá";
}

function tableName(tables: DeductionTable[], id: ID | undefined): string {
  if (id === undefined) return "nenastaveno";
  return tables.find((t) => t.id === id)?.name ?? "neznámá";
}

function sheetName(sheets: ScoringSheet[], id: ID | undefined): string {
  if (id === undefined) return "nenastaveno";
  return sheets.find((s) => s.id === id)?.name ?? "neznámá";
}
